/**
 * HTTP 后端实现 · M3.2 查询域（仅 repository.ts 内部使用，UI 禁止引用）
 * ------------------------------------------------------------
 * 与 EMA-shared ENDPOINTS 一一对应（org / arch / vs / product / tech / ops / dev 七组）。
 * - 每个方法 = 一次真实 HTTP 调用，返回形状与 mock 实现一致（服务端按同一契约计算）
 * - be=false 的方法（本机选择态 / 演示态）不在此登记，由 repository 回落 mock 实现
 * - 组织参数统一序列化为 ?org=<id>：UI 可能传组织画像对象或 id，oid() 归一
 * - 本模块只返回真实 HTTP 结果或抛出错误；现行数据库域不回退 mock
 */
import type { TaskOpinionInput } from '../../../../EMA-shared/src/dto/task'
import {sysRepo} from './sys-repository'
import { apiGet, apiSend } from './http-client'

/** 组织参数归一：画像对象取 id，字符串原样 */
const oid = (o: any): string => (typeof o === 'string' ? o : o?.id || '')
/** 路径段编码 */
const seg = (c: string) => encodeURIComponent(c)

/* ---------- 组织与授权 ---------- */
/* ---------- 架构台账与工作台 ---------- */
export const httpArchImpl = {
  indexes: () => apiGet<any>('/api/arch/indexes'),
  orgApps: (o: any) => apiGet<any[]>('/api/arch/apps', { org: oid(o) }),
  orgLockedGrps: (o: any) => apiGet<any[]>('/api/arch/locked-grps', { org: oid(o) }),
  domStats: (c: string) => apiGet<any>(`/api/arch/domains/${seg(c)}/stats`),
  rpStats: (c: string) => apiGet<any>(`/api/arch/flows/${seg(c)}/stats`),
  epStats: (c: string) => apiGet<any>(`/api/arch/eps/${seg(c)}/stats`),
  ndStats: (c: string) => apiGet<any>(`/api/arch/nodes/${seg(c)}/stats`),
  objStats: (n: string) => apiGet<any>(`/api/arch/objects/${seg(n)}/stats`),
  appModel: (c: string) => apiGet<any>(`/api/arch/apps/${seg(c)}/model`),
  roomModel: (c: string) => apiGet<any>(`/api/arch/rooms/${seg(c)}/model`),
  exOrgRows: (o: any) => apiGet<any[]>('/api/arch/exceptions', { org: oid(o) }),
  ndOrgRows: (o: any) => apiGet<any[]>('/api/arch/node-rows', { org: oid(o) }),
  wbKpis: (o: any) => apiGet<any>('/api/workbench/kpis', { org: oid(o) }),
  wbItems: (o: any) => apiGet<any[]>('/api/workbench/items', { org: oid(o) }),
  wbSay: (o: any) => apiGet<any>('/api/workbench/say', { org: oid(o) }),
  wbArch: (o: any) => apiGet<any>('/api/workbench/arch', { org: oid(o) }),
  mDomBars: (o: any, limit?: number) => apiGet<any[]>('/api/workbench/bars/domains', { org: oid(o), limit }),
  mEpBars: (o: any, limit?: number) => apiGet<any[]>('/api/workbench/bars/eps', { org: oid(o), limit }),
  exOfDom: (c: string, o: any) => apiGet<any[]>(`/api/arch/domains/${seg(c)}/exceptions`, { org: oid(o) }),
  exOfEp: (c: string, o: any) => apiGet<any[]>(`/api/arch/eps/${seg(c)}/exceptions`, { org: oid(o) }),
  phyTables: () => apiGet<any[]>('/api/dev/phy-tables'),
  fullTable: (k: string) => apiGet<any>(`/api/arch/full/${seg(k)}`),
  fullRows: (k: string) => apiGet<any>(`/api/arch/full/${seg(k)}/rows`),

  /* ---- M3.3 命令域：架构 CRUD（一步自动发布，返回 {row, changeId, version, file}）----
     签名与 mock archRepoImpl 完全一致；payload 可携带 `_v` 期望版本 / `_change` 备注，
     服务端剥离后应用。失败（409/400/404/5xx）由 repository 回退 mock（原型宽容语义）。 */
  domCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/domains', p),
  domUpdate: (code: string, p: Record<string, any>) => apiSend<any>('PUT', `/api/arch/cmd/domains/${seg(code)}`, p),
  domRemove: (code: string) => apiSend<any>('DELETE', `/api/arch/cmd/domains/${seg(code)}`),
  grpCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/groups', p),
  grpUpdate: (code: string, p: Record<string, any>) => apiSend<any>('PUT', `/api/arch/cmd/groups/${seg(code)}`, p),
  grpRemove: (code: string) => apiSend<any>('DELETE', `/api/arch/cmd/groups/${seg(code)}`),
  appCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/apps', p),
  appUpdate: (code: string, p: Record<string, any>) => apiSend<any>('PUT', `/api/arch/cmd/apps/${seg(code)}`, p),
  appRemove: (code: string) => apiSend<any>('DELETE', `/api/arch/cmd/apps/${seg(code)}`),
  dgrpCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/data-groups', p),
  dgrpUpdate: (code: string, p: Record<string, any>) => apiSend<any>('PUT', `/api/arch/cmd/data-groups/${seg(code)}`, p),
  dgrpRemove: (code: string) => apiSend<any>('DELETE', `/api/arch/cmd/data-groups/${seg(code)}`),
  dsubCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/data-subjects', p),
  dsubUpdate: (code: string, p: Record<string, any>) => apiSend<any>('PUT', `/api/arch/cmd/data-subjects/${seg(code)}`, p),
  dsubRemove: (code: string) => apiSend<any>('DELETE', `/api/arch/cmd/data-subjects/${seg(code)}`),
  dobjCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/objects', p),
  dobjUpdate: (code: string, p: Record<string, any>) => apiSend<any>('PUT', `/api/arch/cmd/objects/${seg(code)}`, p),
  dobjRemove: (code: string) => apiSend<any>('DELETE', `/api/arch/cmd/objects/${seg(code)}`),
  refCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/refs', p),
  refUpdate: (code: string, p: Record<string, any>) => apiSend<any>('PUT', `/api/arch/cmd/refs/${seg(code)}`, p),
  refRemove: (code: string) => apiSend<any>('DELETE', `/api/arch/cmd/refs/${seg(code)}`),
  abbCreate: (appCode: string, p: Record<string, any>) => apiSend<any>('POST', `/api/arch/cmd/apps/${seg(appCode)}/blocks`, p),
  abbUpdate: (appCode: string, blockCode: string, p: Record<string, any>) =>
    apiSend<any>('PUT', `/api/arch/cmd/apps/${seg(appCode)}/blocks/${seg(blockCode)}`, p),
  abbRemove: (appCode: string, blockCode: string) =>
    apiSend<any>('DELETE', `/api/arch/cmd/apps/${seg(appCode)}/blocks/${seg(blockCode)}`),
  abbSubCreate: (appCode: string, blockCode: string, subName: string) =>
    apiSend<any>('POST', `/api/arch/cmd/apps/${seg(appCode)}/blocks/${seg(blockCode)}/subs`, { subName }),
  abbSubRemove: (appCode: string, blockCode: string, subName: string) =>
    apiSend<any>('DELETE', `/api/arch/cmd/apps/${seg(appCode)}/blocks/${seg(blockCode)}/subs/${seg(subName)}`),
  intgCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/integrations', p),
  intgUpdate: (idx: number | string, p: Record<string, any>) =>
    apiSend<any>('PUT', `/api/arch/cmd/integrations/${seg(String(idx))}`, p),
  intgRemove: (idx: number | string) => apiSend<any>('DELETE', `/api/arch/cmd/integrations/${seg(String(idx))}`),
  relCreate: (p: Record<string, any>) => apiSend<any>('POST', '/api/arch/cmd/rels', p),
  relUpdate: (idx: number, p: Record<string, any>) => apiSend<any>('PUT', `/api/arch/cmd/rels/${seg(String(idx))}`, p),
  relRemove: (idx: number) => apiSend<any>('DELETE', `/api/arch/cmd/rels/${seg(String(idx))}`)
}

/* ---------- 架构总览统计（4A 明细计数 + 工程阶段资产计数） ---------- */
export interface ArchAssetCounts {
  product: { comps: number; demos: number; tpls: number; patterns: number; cfgs: number; menus: number; funcs: number }
  ops: { issueSteps: number; sla: number; plans: number; backups: number; rtoRpo: number }
  spec: { cats: number; docs: number; items: number; passed: number; pending: number; changed: number }
  dev: { apis: number; swModules: number; swFunctions: number; events: number; opsUser: number; opsSys: number; txns: number }
  tech: { netLayers: number; netZones: number; envs: number; deploySvcs: number; swLayers: number }
}
export const fetchArchTableCounts = () =>
  apiGet<{ tables: Record<string, number> }>('/api/arch/v1/counts').then(r => r.tables)
export const fetchArchAssetCounts = () => apiGet<ArchAssetCounts>('/api/arch/v1/asset-counts')

/* ---------- 架构师引擎产物（域间关系模型 / 引擎体检报告，只读） ---------- */
export const fetchDomainRelations = () => apiGet<any>('/api/arch/v1/domain-relations')
export const fetchArchitectHealth = () => apiGet<any>('/api/arch/v1/architect-health')

/* ---------- 业务架构资产台账（价值流 / 业务规则 / 业务校验） ---------- */
export const httpBizImpl = {
  valueStreams: () => apiGet<any[]>('/api/arch/value-streams'),
  bizRules: () => apiGet<any[]>('/api/arch/biz-rules'),
  bizVals: () => apiGet<any[]>('/api/arch/biz-vals')
}

/* ---------- 产品架构（工程阶段） ---------- */
export const httpProductImpl = {
  comps: (cat?: string) => apiGet<any[]>('/api/product/comps', { cat }),
  compsOf: (cat: string) => apiGet<any[]>('/api/product/comps', { cat }),
  compCats: () => apiGet<string[]>('/api/product/comp-cats'),
  demos: () => apiGet<Record<string, string>>('/api/product/demos'),
  tpls: () => apiGet<any[]>('/api/product/tpls'),
  patterns: () => apiGet<any[]>('/api/product/patterns'),
  /** 配置任务台账（按《产品配置手册》模板建模） */
  cfgs: () => apiGet<any[]>('/api/product/cfgs'),
  /** 菜单台账（叶子 id → 页面结构 / 归属应用服务 / 功能描述） */
  pmenus: () => apiGet<any[]>('/api/product/pmenus'),
  /** 功能台账（一个功能一行：业务对象 / 应用架构 L4·L5 / 所属菜单） */
  funcs: () => apiGet<any[]>('/api/product/funcs')
}

/* ---------- 技术架构（4A） ---------- */
export const httpTechImpl = {
  netLayers: () => apiGet<any[]>('/api/tech/net-layers'),
  netZones: () => apiGet<any[]>('/api/tech/net-zones'),
  envs: () => apiGet<any[]>('/api/tech/envs'),
  deploySvcs: () => apiGet<any[]>('/api/tech/deploy-svcs'),
  swLayers: () => apiGet<any[]>('/api/tech/sw-layers')
}

/* ---------- 运维架构（工程阶段） ---------- */
export const httpOpsImpl = {
  issueSteps: () => apiGet<any[]>('/api/ops/issue-steps'),
  sla: () => apiGet<any[]>('/api/ops/sla'),
  plans: () => apiGet<any[]>('/api/ops/plans'),
  backups: () => apiGet<any[]>('/api/ops/backups'),
  rtoRpo: () => apiGet<any[]>('/api/ops/rto-rpo')
}

/* ---------- 开发架构（工程阶段） ---------- */
export const httpDevImpl = {
  /** 网关 API 资产：采集自 EMA-server 真实控制器路由（落库 arch_dev_apis） */
  apis: () => apiGet<any[]>('/api/dev/apis'),
  /** 接口联调工作台的接口契约（真实路由 + 路径参数推导） */
  apiDocs: () => apiGet<any[]>('/api/dev/api-docs'),
  /** 接口所属服务/域聚合（工作台左栏二级） */
  apiDomains: () => apiGet<any[]>('/api/dev/api-domains'),
  swModules: () => apiGet<any[]>('/api/dev/sw-modules'),
  evts: () => apiGet<any[]>('/api/dev/events'),
  opsUser: () => apiGet<any[]>('/api/dev/ops-user'),
  opsSys: () => apiGet<any[]>('/api/dev/ops-sys'),
  txns: () => apiGet<any[]>('/api/dev/txns')
}

/* ---------- 原则规范库（分类 / 文档 / 评审统计） ---------- */
export const httpSpecImpl = {
  cats: () => apiGet<any[]>('/api/spec/cats'),
  docs: (version?:string) => apiGet<any[]>('/api/spec/docs',version?{version}:{}),
  stats: () => apiGet<{ docs: number; items: number; passed: number; pending: number; changed: number }>('/api/spec/stats'),
  /** 一键评审生效：整文档（itemId 省略）或单条目；写失败透传错误，不回退 */
  review: (docId: string, itemId?: string, note?: string) =>
    apiSend<{ ok: boolean; changed: number; skipped: number; docRev: string }>('POST', '/api/spec/review', { docId, itemId, note })
}

/* ---------- 系统配置查看（按菜单维度组织的配置项台账） ---------- */
export const httpSysConfigImpl = {
  items: () => apiGet<any[]>('/api/sys-config/items'),
  modules: () => apiGet<any[]>('/api/sys-config/modules'),
  byId: (id: string) => apiGet<any | undefined>('/api/sys-config/by-id', { id }),
  byMenu: (menuId: string) => apiGet<any[]>('/api/sys-config/by-menu', { menuId }),
  search: (kw: string) => apiGet<any[]>('/api/sys-config/search', { kw })
}

/* ---------- M3.4.1 查询域扩组：只读（写方法不登记，由 repository 回落 mock；M3.4.3 随 DB 落地） ---------- */

/* 企业通讯录：成员档案 profile 是演示态确定性装饰（契约未登记），继续本地派生 */
export const httpDirectoryImpl = {
  members: () => apiGet<any[]>('/api/directory/members'),
  partners: () => apiGet<any[]>('/api/directory/partners'),
  orgTree: (rootLabel?: string) => apiGet<any>('/api/directory/tree', { root: rootLabel }),
  recents: () => apiGet<any[]>('/api/directory/recents')
}

/* 系统功能菜单：leafIndex 按契约随 modules 客户端自建（mock 与投影同源），不登记 */
export const httpMenuImpl = {
  modules: () => apiGet<any[]>('/api/menus/modules')
}

/* 通知中心：platform.notification 服务端持久（列表含已读态；推送由业务事务内触发，无客户端入口） */
export const httpNotifyImpl = {
  list: () => apiGet<any[]>('/api/notifications'),
  markRead: (id: string) => apiSend<void>('POST', `/api/notifications/${seg(id)}/read`),
  markAllRead: () => apiSend<void>('POST', '/api/notifications/read-all')
}

/* 采购管理（演示种子只读） */
export const httpProcImpl = {
  reqs: () => apiGet<any[]>('/api/proc/reqs'),
  orders: () => apiGet<any[]>('/api/proc/orders'),
  sups: () => apiGet<any[]>('/api/proc/sups')
}

/* SaaS 平台管理：P3 角色/权限矩阵已落真实库；其余 CRUD 随 DB 落地逐步接入 */
/** Compatibility facade; all system-management calls share one HTTP implementation. */
export const httpSysImpl = {
  users:sysRepo.users, roles:sysRepo.roles, tenants:sysRepo.tenants,
  notices:sysRepo.notices, audits:sysRepo.audits, permModules:sysRepo.permModules,
  roleSetPerms:sysRepo.roleSetPerms,
  noticeMutate:sysRepo.noticeMutate, noticeHistory:sysRepo.noticeHistory
}

/* 需求与交付：M3.4.1 仅三条只读；状态流转/登记写操作 M3.4.3 */
export const httpRmImpl = {
  reqs: () => apiGet<any[]>('/api/rm/reqs'),
  feats: () => apiGet<any[]>('/api/rm/feats'),
  cases: () => apiGet<any[]>('/api/rm/cases'),
  stats: () => apiGet<any>('/api/rm/stats'),

  reqSave: (p: Record<string, any>) => apiSend<any>('POST', '/api/rm/reqs', p),
  reqUpdateMeta: (id: string, p: Record<string, any>) => apiSend<any>('PUT', `/api/rm/reqs/${seg(id)}`, p),
  reqReview: (id: string, p: Record<string, any>) => apiSend<any>('POST', `/api/rm/reqs/${seg(id)}/review`, p),
  reqUpdatePlan: (id: string, plan: string, scope: string[]) =>
    apiSend<any>('PUT', `/api/rm/reqs/${seg(id)}/plan`, { plan, scope }),
  reqConfirmPlan: (id: string) => apiSend<any>('POST', `/api/rm/reqs/${seg(id)}/confirm-plan`, {}),
  reqSubmitTest: (id: string) => apiSend<any>('POST', `/api/rm/reqs/${seg(id)}/submit-test`, {}),
  reqRelease: (id: string) => apiSend<any>('POST', `/api/rm/reqs/${seg(id)}/release`, {}),
  caseRun: (caseNo: string, pass: boolean) =>
    apiSend<any>('POST', `/api/rm/cases/${seg(caseNo)}/run`, { pass }),
  featAdd: (p: Record<string, any>) => apiSend<any>('POST', '/api/rm/feats', p)
}

/* 任务与待办：work schema 真实读写，无 mock 回落（决定必须落在库上，读失败由页面呈现） */
export const httpTaskImpl = {
  list: (q: { st?: string; kind?: string; kw?: string } = {}) => apiGet<any[]>('/api/tasks', q),
  stats: () => apiGet<any>('/api/tasks/stats'),
  /** 任务页四页签徽标计数（一次聚合；待了解含公告与会议纪要 id） */
  tabCounts: (org?: string) => apiGet<any>('/api/tasks/tab-counts', org ? { org } : {}),
  /** 平台审批中心：业务系统推送的待审批事项（task.approval，只读平台库） */
  approvals: () => apiGet<any>('/api/tasks/approvals'),
  notices: () => apiGet<any[]>('/api/tasks/notices'),
  awareness: () => apiGet<{rows:any[];errors:Record<string,string>}>('/api/tasks/awareness'),
  readAwareness: (items: {kind:string;id:string;version:string}[]) => apiSend<any>('POST','/api/tasks/awareness/read',{items}),
  detail: (no: string) => apiGet<any>(`/api/tasks/${seg(no)}`),
  create: (p: Record<string, any>) => apiSend<any>('POST', '/api/tasks', p),
  decide: (no: string, p: Record<string, any>) => apiSend<any>('POST', `/api/tasks/${seg(no)}/decision`, p),
  /* 联合决策：@邀请同事表态 / 受邀人提交或更新自己的表态（投票+留言） */
  inviteOpinions: (no: string, personIds: string[]) =>
    apiSend<{ invited: number; opinions: any[] }>('POST', `/api/tasks/${seg(no)}/opinions/invite`, { person_ids: personIds }),
  opine: (no: string, p: TaskOpinionInput) =>
    apiSend<{ opinions: any[] }>('POST', `/api/tasks/${seg(no)}/opinion`, p)
}

/* 方案架构四层 */
export const httpSolImpl = {
  arch: () => apiGet<{ domains: any[]; industries: any[]; sols: any[]; packs: any[] }>('/api/sol/arch')
}

/* 交付实例 */
export const httpDeliveryImpl = {
  list: () => apiGet<any[]>('/api/delivery/cases')
}

/* 交付流水线：M3.4.1 仅只读；门禁/创建运行写操作 M3.4.3 */
export const httpPipelineImpl = {
  list: () => apiGet<any[]>('/api/pipeline/runs')
}
